import { Box, Grid, useTheme, TextField } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ButtonV1,
  DropdownV1,
  DatePickerV1,
  MultiSelectSearchDropdown,
} from "components";
import { DELIVERY_TYPE, FG_READINESS, ORDER_TYPES } from "../constants";
import {
  getMaxToDate,
  getUniqueStatesFromSellerData,
  getCompanyNamesFromSellerData,
} from "../makeData";
import PropTypes from "prop-types";

const deliveryTypes = Object.values(DELIVERY_TYPE);

const CustomTextField = memo(
  ({ name, label, control, digitsOnly = false, maxLength }) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            fullWidth
            label={label}
            inputProps={{ maxLength }}
            onChange={(e) => {
              const value = digitsOnly
                ? e.target.value.replace(/\D/g, "")
                : e.target.value;

              field.onChange(value);
            }}
          />
        )}
      />
    );
  },
);

const Filter = ({
  onSubmit,
  onReset,
  sellerData = [],
  customersData = [],
  fetchCustomersData,
  buyerLoading,
}) => {
  const theme = useTheme();
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      buyerOrderNo: "",
      sellerOrderNo: "",
      sellerNames: [],
      sellerStates: [],
      customerNames: [],
      deliveryType: "ALL",
      orderType: "ALL",
      dateType: "Order confirmation",
      dateFrom: "",
      dateTo: "",
      doId: "",
    },
  });

  const fromDate = watch("dateFrom");
  const sellerNameOptions = useMemo(
    () => getCompanyNamesFromSellerData(sellerData),
    [sellerData],
  );
  const sellerStateOptions = useMemo(
    () => getUniqueStatesFromSellerData(sellerData),
    [sellerData],
  );
  const customerOptions = useMemo(() => customersData ?? [], [customersData]);
  const maxToDate = useMemo(() => getMaxToDate(fromDate), [fromDate]);

  const handleReset = useCallback(() => {
    reset();
    onReset?.();
  }, [onReset, reset]);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey["400"]}`,
        borderRadius: 2,
        padding: 3,
        mb: 3,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <CustomTextField
              name="buyerOrderNo"
              label="Buyer order no"
              control={control}
            />
          </Grid>

          <Grid item xs={2}>
            <CustomTextField
              name="sellerOrderNo"
              label="Seller order no"
              control={control}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="sellerNames"
              control={control}
              render={({ field }) => (
                <MultiSelectSearchDropdown
                  {...field}
                  sx={{ flex: 1 }}
                  label="Source Seller"
                  selectAll
                  options={sellerNameOptions}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="sellerStates"
              control={control}
              render={({ field }) => (
                <MultiSelectSearchDropdown
                  {...field}
                  sx={{ flex: 1 }}
                  label="Seller States"
                  selectAll
                  options={sellerStateOptions}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="customerNames"
              control={control}
              render={({ field }) => (
                <MultiSelectSearchDropdown
                  {...field}
                  sx={{ flex: 1 }}
                  label="Buyer name"
                  options={customerOptions}
                  onSearch={fetchCustomersData}
                  loading={buyerLoading}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="deliveryType"
              control={control}
              render={({ field }) => (
                <DropdownV1
                  {...field}
                  placeholder=""
                  itemList={deliveryTypes}
                  listType="array"
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="orderType"
              control={control}
              render={({ field }) => (
                <DropdownV1
                  {...field}
                  placeholder=""
                  itemList={ORDER_TYPES}
                  listType="array"
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="dateType"
              control={control}
              render={({ field }) => (
                <DropdownV1
                  {...field}
                  placeholder=""
                  itemList={FG_READINESS}
                  listType="array"
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="dateFrom"
              control={control}
              render={({ field }) => (
                <DatePickerV1
                  value={field.value}
                  onChange={field.onChange}
                  disableFuture
                  label="From date"
                  inputProps={{
                    placeholder: "dd-mm-yyyy",
                  }}
                  dateStyle={{ width: "100%" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <Controller
              name="dateTo"
              control={control}
              render={({ field }) => (
                <DatePickerV1
                  value={field.value}
                  onChange={field.onChange}
                  label="To date"
                  disableFuture
                  minDate={fromDate}
                  maxDate={maxToDate}
                  inputProps={{
                    placeholder: "dd-mm-yyyy",
                  }}
                  dateStyle={{ width: "100%" }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <CustomTextField name="doId" label="Do ID" control={control} />
          </Grid>

          <Grid
            item
            xs={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1.5}
            sx={{ minWidth: "190px", flexShrink: 0 }}
          >
            <ButtonV1
              title="Submit"
              type="submit"
              variant="contained"
              sx={{ minWidth: "87px", px: 1, fontSize: 12 }}
            />

            <ButtonV1
              title="Reset"
              variant="outlined"
              onClick={handleReset}
              sx={{ minWidth: "87px", px: 1, fontSize: 12 }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

Filter.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  sellerData: PropTypes.array,
  customersData: PropTypes.array,
  fetchCustomersData: PropTypes.func,
  buyerLoading: PropTypes.bool,
};

export default memo(Filter);